import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import AlertsBadge from '#/transit-tracker/components/AlertsBadge';
import { alertType, arrivalType, routeType } from '#/prop-types';
import {
  getArrivingVehicleType,
  getArrivalScheduledLabel,
} from '#/transit-tracker/utils/arrivals';
import useEstimatedArrivalText from '#/transit-tracker/hooks/useEstimatedArrivalText';
import { STREETCAR_SUB_TYPE, WES_SUB_TYPE } from '#/transit-tracker/constants';
import useIntlDatetime from '#/shared/hooks/useIntlDatetime';
import * as Styled from './styled';

function StopSignPanel({
  sign,
  navigateToRouteAlerts,
  navigateToVehicleTracker,
  navigateToSchedule,
}) {
  const { arrivals } = sign;
  const { getEstimatedArrivalText } = useEstimatedArrivalText();

  const routeSubType = sign.route?.routeSubType;
  const vehicleIsTrackable = ![STREETCAR_SUB_TYPE, WES_SUB_TYPE].includes(
    routeSubType
  );

  const { milliSecondsToTriMetTimeString } = useIntlDatetime();

  const getTimeBadge = arrival => {
    switch (arrival.status) {
      case 'estimated': {
        const { arrivalText, arrivalDue } = getEstimatedArrivalText(arrival);

        let arrivingVehicleTime;
        let arrivingVehicleTypeText;

        if (routeSubType !== STREETCAR_SUB_TYPE) {
          arrivingVehicleTime = `${milliSecondsToTriMetTimeString(
            arrival.scheduledTime
          )} `;
          arrivingVehicleTypeText = getArrivingVehicleType(arrival);
        } else if (arrivalDue) {
          arrivingVehicleTypeText = (
            <FormattedMessage id="arrivals.next-streetcar" />
          );
        } else {
          arrivingVehicleTypeText = (
            <FormattedMessage id="arrivals.streetcar-arriving" />
          );
        }

        const TextWrapper =
          (arrivalDue && Styled.DueText) ||
          (arrival.showMilesAway && Styled.MilesAwayText) ||
          Styled.EstimatedTimeText;

        return (
          <>
            <Styled.ButtonDetailWrap>
              <Styled.ButtonDetail>
                {arrivingVehicleTime} {arrivingVehicleTypeText}
              </Styled.ButtonDetail>
              <Styled.ButtonEmphasis>
                <TextWrapper>{arrivalText}</TextWrapper>
              </Styled.ButtonEmphasis>
            </Styled.ButtonDetailWrap>
            {vehicleIsTrackable && (
              <Styled.TrackButton dropOffOnly={arrival.dropOffOnly}>
                <FormattedMessage
                  id={
                    arrival.dropOffOnly
                      ? 'arrivals.track-btn-drop-Off-Only'
                      : 'arrivals.track_btn'
                  }
                />
              </Styled.TrackButton>
            )}
          </>
        );
      }
      case 'scheduled': {
        return (
          <>
            <Styled.ButtonDetail>
              {getArrivalScheduledLabel(arrival.scheduledTime)}
            </Styled.ButtonDetail>
            <Styled.ButtonEmphasis>
              <Styled.ScheduledArrivalText>
                {milliSecondsToTriMetTimeString(arrival.scheduledTime)}
              </Styled.ScheduledArrivalText>
            </Styled.ButtonEmphasis>
          </>
        );
      }
      case 'canceled': {
        return (
          <>
            <Styled.CanceledLabel>
              {milliSecondsToTriMetTimeString(arrival.scheduledTime)}{' '}
              {getArrivingVehicleType(arrival)}
            </Styled.CanceledLabel>
            <Styled.ButtonEmphasis>
              <Styled.CanceledTimeText>
                <FormattedMessage id="arrivals.canceled" />
              </Styled.CanceledTimeText>
            </Styled.ButtonEmphasis>
          </>
        );
      }
      default:
        return '';
    }
  };

  return (
    <Styled.SignExpansionPanel>
      <Styled.ArrivalsList>
        {arrivals.slice(0, 3).map(a => {
          const arrivalIsEstimated = a.status === 'estimated';
          const onlyNavigateToVehicleIfTrackable = vehicleIsTrackable
            ? () => navigateToVehicleTracker(a.route.id, a.blockId)
            : null;
          return (
            <Styled.Arrival
              key={a.id}
              isEstimated={arrivalIsEstimated}
              disabled={!arrivalIsEstimated || !vehicleIsTrackable}
              onClick={onlyNavigateToVehicleIfTrackable}
              dropOffOnly={a.dropOffOnly}
            >
              <ScreenReaderText>
                <FormattedMessage id="arrivals.arrivaltime_acc" />
              </ScreenReaderText>
              {getTimeBadge(a)}
            </Styled.Arrival>
          );
        })}
      </Styled.ArrivalsList>

      <Styled.FullScheduleAlerts>
        <Styled.FullScheduleColumn>
          <Styled.ViewFullScheduleLink
            onClick={() => navigateToSchedule(sign.route.id)}
          >
            <FormattedMessage id="arrivals.fullschedule" />
          </Styled.ViewFullScheduleLink>
        </Styled.FullScheduleColumn>
        {sign.alerts.length > 0 && (
          <Styled.AlertsColumn>
            <Styled.AlertButton
              onClick={() => navigateToRouteAlerts(sign.route.id)}
            >
              <Styled.AlertsBadge>
                <AlertsBadge size={25} />
              </Styled.AlertsBadge>
              <Styled.AlertCount>
                <ScreenReaderText>
                  <FormattedMessage id="arrivals.readalerts_acc" />
                </ScreenReaderText>
                {sign.alerts.length}&nbsp;
                {sign.alerts.length === 1 ? (
                  <FormattedMessage id="arrivals.alert" />
                ) : (
                  <FormattedMessage id="arrivals.alerts" />
                )}
              </Styled.AlertCount>
            </Styled.AlertButton>
          </Styled.AlertsColumn>
        )}
      </Styled.FullScheduleAlerts>
    </Styled.SignExpansionPanel>
  );
}

StopSignPanel.propTypes = {
  /** Contains arrivals and route information about a particular sign at a stop */
  sign: PropTypes.shape({
    vanityName: PropTypes.string.isRequired,
    alerts: PropTypes.arrayOf(alertType).isRequired,
    arrivals: PropTypes.arrayOf(arrivalType),
    route: routeType.isRequired,
  }).isRequired,
  navigateToVehicleTracker: PropTypes.func.isRequired,
  /** Sends the user to a routes specific stop Schedule page */
  navigateToSchedule: PropTypes.func.isRequired,
  /** Sends the user to a specific route alerts page */
  navigateToRouteAlerts: PropTypes.func.isRequired,
};

export default StopSignPanel;
